import { useState } from 'react';
import { Link } from 'react-router-dom';
import FavoriteButton from '@/components/feature/FavoriteButton';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import {
  experienceCategories,
  verifiedProperties,
  type PropertyCommunity,
  type VerifiedProperty,
} from '@/mocks/properties';
import { propertyRoute } from '@/mocks/propertyRoutes';

type MapPoint = {
  id: string;
  x: number;
  y: number;
  label: string;
  labelAlign?: 'left' | 'center' | 'right';
};

const mapPoints: MapPoint[] = [
  { id: 'sunset-bungalow-30a', x: 6, y: 76, label: 'Dune Allen', labelAlign: 'left' },
  { id: 'hannahs-pearl', x: 38, y: 61, label: 'WaterSound West' },
  { id: 'belvedere', x: 48, y: 55, label: 'Rosemary Beach' },
  { id: 'beach-beach-baby', x: 56, y: 43, label: 'West End PCB' },
  { id: 'bella-vita', x: 75, y: 35, label: 'Emerald Beach' },
  { id: 'sea-dreams', x: 88, y: 25, label: 'Thomas Drive', labelAlign: 'right' },
];

const propertyById = new Map(verifiedProperties.map((property) => [property.id, property]));

type AreaFilter = 'All' | PropertyCommunity;

const areaRoutes: Record<AreaFilter, string> = {
  All: '/vacation-search-results',
  'Panama City Beach': '/pcb-area-search',
  '30A': '/30a-area-search',
  'Rosemary Beach': '/rosemary-beach-search',
  WaterSound: '/watersound',
  Watercolor: '/watercolor-area',
  'Dune Allen': '/dune-allen',
  'Santa Rosa Beach': '/santa-rosa-beach',
};

const areaFilters: AreaFilter[] = [
  'All',
  'Panama City Beach',
  '30A',
  'Rosemary Beach',
  'WaterSound',
  'Dune Allen',
  'Santa Rosa Beach',
];

const amenityRoutes: Record<string, string> = {
  'gulf-front': '/gulf-front-search-results',
  pool: '/pool-search-results',
  'large-groups': '/large-groups-search-results',
  'outdoor-living': '/outdoor-living-search-results',
  'golf-cart': '/golf-cart-search-results',
  'pet-friendly': '/pets-search-results',
};

const chipStyle =
  'bg-[#EFE4D6] text-foreground-700 border-[#DED0BE] hover:border-[#B99F87] hover:text-foreground-950';

function CompactPropertyCard({
  property,
  active,
  onEnter,
  onLeave,
}: {
  property: VerifiedProperty;
  active: boolean;
  onEnter: () => void;
  onLeave: () => void;
}) {
  return (
    <article
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
      className={`group rounded-lg border bg-background-50 transition-all duration-300 ${
        active
          ? 'border-accent-500 shadow-[0_22px_50px_rgba(7,15,18,0.14)]'
          : 'border-background-300 shadow-[0_14px_34px_rgba(7,15,18,0.07)] hover:border-[#B99F87]'
      }`}
    >
      <div className="relative aspect-[16/10] overflow-hidden rounded-t-lg bg-background-200">
        <img
          src={property.image}
          alt={property.name}
          className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-foreground-950/35 to-transparent" />
        <FavoriteButton id={property.id} className="absolute right-3 top-3 z-10" />
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <h2 className="font-heading text-2xl leading-tight text-foreground-950">
            {property.name}
          </h2>
          <div className="max-w-[45%] pt-1 text-right text-[10px] uppercase tracking-[0.15em] text-foreground-500">
            {property.location}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 border-y border-background-200 py-3 text-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-foreground-500">
              Beds
            </div>
            <div className="mt-1 text-sm text-foreground-950">{property.bedrooms}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-foreground-500">
              Baths
            </div>
            <div className="mt-1 text-sm text-foreground-950">{property.bathrooms}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-foreground-500">
              Sleeps
            </div>
            <div className="mt-1 text-sm text-foreground-950">Up to {property.sleeps}</div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
          {property.highlights.slice(3, 6).map((highlight, index, highlights) => (
            <span key={highlight} className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-[0.13em] text-foreground-700">
                {highlight}
              </span>
              {index < highlights.length - 1 && (
                <span className="h-4 w-px bg-background-300" aria-hidden="true" />
              )}
            </span>
          ))}
        </div>
        <Link
          to={propertyRoute(property.id)}
          className="mt-5 inline-flex items-center gap-2 rounded-lg bg-foreground-950 px-4 py-2.5 text-sm text-background-50 transition-colors hover:bg-accent-600"
        >
          View property
          <i className="ri-arrow-right-line" />
        </Link>
      </div>
    </article>
  );
}

function BrowseControls() {
  const availableAreaFilters = areaFilters.filter(
    (area) => area === 'All' || verifiedProperties.some((property) => property.communities.includes(area)),
  );

  return (
    <div className="grid grid-cols-1 gap-4 md:gap-5 xl:grid-cols-2">
      <div className="rounded-lg border border-background-300 bg-background-100/70 p-5">
        <p className="mb-3 text-[11px] uppercase tracking-[0.2em] text-foreground-700">
          Explore by area
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {availableAreaFilters.map((label) => (
            <Link
              key={label}
              to={areaRoutes[label]}
              className={`rounded-lg border px-3 py-2 text-[11px] uppercase tracking-[0.18em] transition-colors ${chipStyle}`}
            >
              {label}
            </Link>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-background-300 bg-background-100/70 p-5">
        <p className="mb-3 text-[11px] uppercase tracking-[0.2em] text-foreground-700">
          Explore by amenities
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {experienceCategories.map((category) => (
            <Link
              key={category.id}
              to={amenityRoutes[category.id] ?? '/vacation-search-results'}
              className={`rounded-lg border px-3 py-2 text-[11px] uppercase tracking-[0.18em] transition-colors ${chipStyle}`}
            >
              {category.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function CoastalMap({
  activeId,
  setActiveId,
}: {
  activeId: string | null;
  setActiveId: (id: string | null) => void;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-background-300 bg-[#edf4f1] shadow-[0_22px_60px_rgba(7,15,18,0.10)]">
      <div className="flex items-center justify-between border-b border-background-300 bg-background-50/90 px-5 py-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-accent-700">
            Map search
          </div>
          <div className="mt-1 font-heading text-2xl leading-none text-foreground-950">
            Emerald Coast
          </div>
        </div>
        <div className="rounded-lg bg-foreground-950 px-3 py-2 text-[10px] uppercase tracking-[0.14em] text-background-50">
          6 stays
        </div>
      </div>

      <div className="relative h-[420px] md:h-[540px]">
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <rect width="100" height="100" fill="#d9ece8" />
          <path d="M0 72 C17 65 27 70 38 58 C49 46 57 45 69 35 C80 26 88 17 100 10 L100 100 L0 100 Z" fill="#53bfc4" opacity="0.55" />
          <path d="M0 78 C18 70 28 76 41 62 C52 50 59 49 72 38 C84 28 90 20 100 15" fill="none" stroke="#fff7ed" strokeWidth="6" />
          <path d="M0 75 C18 68 28 73 40 60 C51 48 58 47 71 36 C82 27 89 18 100 13" fill="none" stroke="#b99f87" strokeWidth="0.7" opacity="0.75" />
          <path d="M5 68 C20 62 31 67 42 55 C52 45 59 44 70 34 C80 25 88 18 96 13" fill="none" stroke="#6e7a82" strokeWidth="0.35" strokeDasharray="2 2" opacity="0.65" />
          <path d="M20 0 C22 13 23 25 22 36 C22 41 22 45 21 47" fill="none" stroke="#7a6a5a" strokeWidth="0.7" opacity="0.68" />
          <path d="M9 48 C9 55 9 62 9 71" fill="none" stroke="#7a6a5a" strokeWidth="0.7" opacity="0.68" />
          <path d="M60 0 C61 11 60 22 61 32 C61 35 61 37 62 39" fill="none" stroke="#7a6a5a" strokeWidth="0.7" opacity="0.68" />
          <path d="M0 48 C14 47 28 47 42 45 C48 44 51 44 54 44 C56 44 58 43 60 41 C68 35 78 25 88 15 C94 11 98 9 100 8" fill="none" stroke="#7a6a5a" strokeWidth="0.7" opacity="0.68" />
        </svg>

        <div className="absolute left-[15%] top-[37%] rounded-md border border-background-300 bg-background-50/90 px-2 py-1 text-[10px] font-medium tracking-[0.08em] text-foreground-700 shadow-sm">
          331
        </div>
        <div className="absolute left-[5%] top-[61%] rounded-md border border-background-300 bg-background-50/90 px-2 py-1 text-[10px] font-medium tracking-[0.08em] text-foreground-700 shadow-sm">
          393
        </div>
        <div className="absolute left-[56%] top-[22%] rounded-md border border-background-300 bg-background-50/90 px-2 py-1 text-[10px] font-medium tracking-[0.08em] text-foreground-700 shadow-sm">
          79
        </div>
        <div className="absolute left-[9%] top-[43%] rounded-md border border-background-300 bg-background-50/90 px-2 py-1 text-[10px] font-medium tracking-[0.08em] text-foreground-700 shadow-sm">
          98
        </div>
        <div className="absolute left-[62%] top-[26%] md:top-[31%] rounded-lg bg-background-50/86 px-3 py-2 text-[10px] uppercase tracking-[0.16em] text-foreground-700 shadow-sm">
          Pier Park
        </div>
        <div className="absolute left-[31%] top-[57%] rounded-lg bg-background-50/86 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-foreground-700 shadow-sm">
          30A
        </div>
        <div className="absolute left-[1%] top-[66%] md:top-[64%] rounded-lg bg-background-50/86 px-3 py-2 text-[10px] uppercase tracking-[0.14em] text-foreground-700 shadow-sm">
          Dune Allen
        </div>
        <div className="absolute left-[13%] md:left-[31%] top-[51%] rounded-lg bg-background-50/86 px-3 py-2 text-[10px] uppercase tracking-[0.14em] text-foreground-700 shadow-sm">
          WaterSound
        </div>
        <div className="absolute left-[35%] md:left-[39%] top-[45%] rounded-lg bg-background-50/86 px-3 py-2 text-[10px] uppercase tracking-[0.14em] text-foreground-700 shadow-sm">
          Rosemary Beach
        </div>
        <div className="absolute right-[11%] top-[15%] rounded-lg bg-background-50/86 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-foreground-700 shadow-sm">
          Panama City Beach
        </div>
        <div className="absolute bottom-8 left-8 font-heading text-4xl italic text-background-50/76">
          Gulf of Mexico
        </div>

        {mapPoints.map((point) => {
          const property = propertyById.get(point.id);
          const active = activeId === point.id;
          const labelPosition =
            point.labelAlign === 'left'
              ? 'left-0 translate-x-0'
              : point.labelAlign === 'right'
                ? 'right-0 translate-x-0'
                : 'left-1/2 -translate-x-1/2';

          return (
            <button
              key={point.id}
              type="button"
              onMouseEnter={() => setActiveId(point.id)}
              onMouseLeave={() => setActiveId(null)}
              onFocus={() => setActiveId(point.id)}
              onBlur={() => setActiveId(null)}
              className="absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer text-left"
              style={{ left: `${point.x}%`, top: `${point.y}%` }}
              aria-label={property ? `Show ${property.name} on map` : point.label}
            >
              <span
                className={`relative flex h-9 w-9 items-center justify-center rounded-full border-2 transition-all duration-300 ${
                  active
                    ? 'scale-125 border-background-50 bg-accent-600 text-background-50 shadow-[0_14px_32px_rgba(7,15,18,0.28)]'
                    : 'border-background-50 bg-foreground-950 text-background-50 shadow-[0_10px_24px_rgba(7,15,18,0.20)]'
                }`}
              >
                <i className="ri-home-5-line text-base" />
              </span>
              <span
                className={`absolute top-11 w-max max-w-[190px] rounded-lg border border-background-300 bg-background-50 px-3 py-2 text-center shadow-[0_12px_28px_rgba(7,15,18,0.16)] transition-all duration-300 ${labelPosition} ${
                  active ? 'opacity-100 translate-y-0' : 'pointer-events-none opacity-0 -translate-y-1'
                }`}
              >
                <span className="block font-heading text-lg leading-none text-foreground-950">
                  {property?.name}
                </span>
                <span className="mt-1 block text-[10px] uppercase tracking-[0.16em] text-foreground-500">
                  {point.label}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function MapSearchPage() {
  const [activeId, setActiveId] = useState<string | null>(null);

  return (
    <main className="bg-background-50 text-foreground-900 font-body">
      <Header />

      <section className="bg-background-50 pt-32 md:pt-40 pb-10 md:pb-14">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,0.48fr)_minmax(0,0.52fr)] lg:items-end">
            <div>
              <div className="mb-5 text-[11px] uppercase tracking-[0.24em] text-accent-700">
                <span className="mr-3 inline-block h-px w-6 bg-accent-500 align-middle" />
                Emerald Coast
              </div>
              <h1 className="max-w-4xl font-heading text-5xl leading-[0.98] text-foreground-950 md:text-7xl">
                See the collection by location.
              </h1>
              <p className="mt-5 max-w-2xl text-base font-light leading-relaxed text-foreground-600 md:text-lg">
                Browse the featured Inspire stays by area and see how each property sits along
                the Emerald Coast.
              </p>
            </div>
            <div>
              <BrowseControls />
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10">
          <CoastalMap activeId={activeId} setActiveId={setActiveId} />

          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {verifiedProperties.map((property) => (
              <CompactPropertyCard
                key={property.id}
                property={property}
                active={activeId === property.id}
                onEnter={() => setActiveId(property.id)}
                onLeave={() => setActiveId(null)}
              />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
